import React, { useId } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import '../../../styles/PerformerCard.css';

/** First letters of up to two words, for a band without an avatar. */
const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

/**
 * The two-tone performer card at the top of the right column (mockup 01,
 * ENG-667): avatar, name, "N watching · requests open", the LIVE pill, and a
 * row of crate covers ending in a "+N" tile that opens the crate.
 */
const PerformerCard = ({ band, crate, onOpenCrate, followControl = null }) => {
  const { t, format } = useLocale();
  const nameId = useId();
  if (!band) return null;

  const avatar = resolveMediaUrl(band.avatarUrl);
  const covers = (crate?.artwork ?? []).slice(0, 4).map(resolveMediaUrl).filter(Boolean);
  const more = Math.max(0, (crate?.totalSongs ?? 0) - covers.length);
  const watching = t('stream.performer.watching', { count: band.viewerCount, formattedCount: format.number(band.viewerCount) });
  const requestState = band.isLive
    ? t(band.requestsOpen ? 'stream.performer.requestsOpen' : 'stream.performer.requestsClosed')
    : t('stream.performer.offline');

  return (
    <section className="performer-card" data-testid="performer-card" aria-labelledby={nameId}>
      <header className="performer-card-top">
        {avatar
          ? <img src={avatar} alt="" className="performer-card-avatar" />
          : <span className="performer-card-avatar performer-card-avatar--initials" aria-hidden="true">{initialsOf(band.name)}</span>}
        <div className="performer-card-heading">
          <h1 className="performer-card-name" id={nameId}>{band.name}</h1>
          <p className="performer-card-meta" data-testid="performer-card-meta">
            {band.isLive ? t('stream.performer.liveMeta', { watching, requests: requestState }) : requestState}
          </p>
        </div>
        {band.isLive && (
          <span className="performer-card-live" data-testid="performer-card-live">{t('stream.performer.live')}</span>
        )}
        {followControl}
      </header>
      {(covers.length > 0 || more > 0) && (
        <button
          type="button"
          className="performer-card-crate"
          onClick={onOpenCrate}
          aria-label={t('stream.crate.open', { count: crate.totalSongs, formattedCount: format.number(crate.totalSongs) })}
        >
          {covers.map((src, index) => (
            <img key={`${src}-${index}`} src={src} alt="" className="performer-card-cover" />
          ))}
          {more > 0 && (
            <span className="performer-card-cover performer-card-more" aria-hidden="true">
              {t('stream.crate.more', { formattedCount: format.number(more) })}
            </span>
          )}
        </button>
      )}
    </section>
  );
};

export default PerformerCard;
